import { Controller } from "@hotwired/stimulus"

// Shopping list email form (Phase 1 item 1.3).
//
// Lives next to the recipe-scaler controller; listens on the document for
// `recipe-scaler:change` so the request body always carries the user's
// scaled-up servings number, not the recipe default.
export default class extends Controller {
  static targets = [ "email", "submit", "banner", "form", "servingsInput", "trap" ]
  static values = {
    submitUrl: String,
    initialServings: { type: Number, default: 0 }
  }

  connect() {
    this._currentServings = this.initialServingsValue
    if (this.hasServingsInputTarget) {
      this.servingsInputTarget.value = String(this._currentServings)
    }

    this._onScalerChange = (event) => {
      const v = parseInt(event.detail?.servings, 10)
      if (!isNaN(v) && v > 0) {
        this._currentServings = v
        if (this.hasServingsInputTarget) {
          this.servingsInputTarget.value = String(v)
        }
      }
    }
    document.addEventListener("recipe-scaler:change", this._onScalerChange)
  }

  disconnect() {
    if (this._onScalerChange) {
      document.removeEventListener("recipe-scaler:change", this._onScalerChange)
    }
  }

  async submit(event) {
    event.preventDefault()
    this._clearBanner()

    const email = (this.hasEmailTarget ? this.emailTarget.value : "").trim()
    if (!this._isValidEmail(email)) {
      this._showBanner("Please enter a valid email address.", "error")
      return
    }

    this._setSubmitting(true)
    try {
      const trapValue = this.hasTrapTarget ? this.trapTarget.value : ""
      const response = await fetch(this.submitUrlValue, {
        method: "POST",
        headers: this._jsonHeaders(),
        body: JSON.stringify({ email, servings: this._currentServings, website: trapValue })
      })
      const data = await response.json().catch(() => ({}))
      if (response.ok && data.status === "sent") {
        this._showBanner(
          `Sent. Check ${email} for your shopping list.`,
          "success"
        )
        if (this.hasFormTarget) this.formTarget.reset()
      } else {
        this._showBanner(data.message || "Something went wrong — please try again.", "error")
      }
    } catch (err) {
      this._showBanner("Network error — please try again.", "error")
    } finally {
      this._setSubmitting(false)
    }
  }

  _setSubmitting(submitting) {
    if (!this.hasSubmitTarget) return
    this.submitTarget.disabled = submitting
    this.submitTarget.textContent = submitting ? "Sending…" : this.submitTarget.dataset.label || "Email me the list"
  }

  _showBanner(message, kind) {
    if (!this.hasBannerTarget) return
    this.bannerTarget.textContent = message
    this.bannerTarget.classList.remove("hidden", "text-red-700", "text-emerald-700", "bg-red-50", "bg-emerald-50", "border-red-200", "border-emerald-200")
    if (kind === "success") {
      this.bannerTarget.classList.add("text-emerald-700", "bg-emerald-50", "border-emerald-200")
    } else {
      this.bannerTarget.classList.add("text-red-700", "bg-red-50", "border-red-200")
    }
  }

  _clearBanner() {
    if (!this.hasBannerTarget) return
    this.bannerTarget.textContent = ""
    this.bannerTarget.classList.add("hidden")
  }

  _isValidEmail(value) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)
  }

  _jsonHeaders() {
    return {
      "Content-Type": "application/json",
      "Accept": "application/json",
      "X-CSRF-Token": this._csrfToken()
    }
  }

  _csrfToken() {
    const meta = document.querySelector('meta[name="csrf-token"]')
    return meta ? meta.content : ""
  }
}
